import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PhotographerListItem from '../PhotographersList/PhotographerListItem';
import { BookedEventType, CalendarRowSize, Order, Photographer } from '../../BookingsCalendarTypes';
import { Translate } from '../../../../../shared/LanguageService';
import MapComponent from '../Map/MapComponent';
import Dialog from '@mui/material/Dialog';
import MapIcon from '@mui/icons-material/Map';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

type PhotographerListLabels = {
    calendarPhotographersHeading: string;
    sms: string;
    email: string;
    route: string;
};

type ExternalProps = {
    photographers: Photographer[];
    selectedOrder: Order;
    firstPhotographerId: number;
    activeRow: number;
};

type PhotographersListProps = {
    labels: PhotographerListLabels;
} & ExternalProps;

const photographersHeaderStyle = {
    backgroundColor: 'white',
    fontWeight: 700,
    fontSize: '15px',
    height: '35px',
    width: '100%',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center'
};

const PhotographersList = (props: PhotographersListProps) => {
    let { photographers, labels, selectedOrder, firstPhotographerId, activeRow } = props;
    const [isMapDialogOpen, setIsMapDialogOpen] = React.useState(false);

    const handleOpenMapDialog = () => {
        setIsMapDialogOpen(true);
    };

    const handleCloseMapDialog = () => {
        setIsMapDialogOpen(false);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '300px'
            }}
        >
            <Typography sx={photographersHeaderStyle}>
                {labels.calendarPhotographersHeading}
                {selectedOrder ? (
                    <IconButton
                        sx={{ padding: '3px', marginLeft: '7px' }}
                        onClick={handleOpenMapDialog}
                    >
                        <MapIcon />
                    </IconButton>
                ) : null}
            </Typography>
            {selectedOrder ? (
                <Dialog
                    open={isMapDialogOpen}
                    onClose={handleCloseMapDialog}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        style: {
                            height: '70%',
                            width: '70%',
                            maxWidth: 'none'
                        }
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseMapDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            backgroundColor: 'white',
                            zIndex: 99,
                            ':hover': {
                                backgroundColor: 'white',
                                color: 'rgb(51 51 51)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <MapComponent selectedOrder={selectedOrder} photographers={photographers} />
                </Dialog>
            ) : null}
            {photographers.map((photographer, index) => {
                let isOnFirstRow = index === 0;
                let isOnLastRow = index === photographers.length - 1;

                let hasSmallRowSize =
                    photographer.bookedEvents.filter(
                        (event) =>
                            event.type === BookedEventType.se360Booking ||
                            event.type === BookedEventType.scandphotosBooking ||
                            event.type === BookedEventType.blocked
                    ).length < 1;

                let hasLargeRowSize = photographer.id === firstPhotographerId;
                let isActiveRow = activeRow === index;

                return (
                    <Box
                        key={photographer.id}
                        aria-label={'photographer-list-column'}
                        sx={{
                            gridColumn: '1',
                            width: '100%',
                            height:
                                hasLargeRowSize || isActiveRow
                                    ? CalendarRowSize.large
                                    : hasSmallRowSize && !isActiveRow
                                    ? CalendarRowSize.small
                                    : CalendarRowSize.medium,
                            padding: isOnFirstRow
                                ? '0px 3px 2px 0px'
                                : isOnLastRow
                                ? '1px 3px 0px 0px'
                                : '1px 3px 2px 0px',
                            backgroundColor: 'white'
                        }}
                    >
                        <PhotographerListItem
                            photographer={photographer}
                            labels={labels}
                            hasSmallRowSize={hasSmallRowSize && !hasLargeRowSize}
                            hasLargeRowSize={hasLargeRowSize}
                            isActiveRow={isActiveRow}
                        />
                    </Box>
                );
            })}
        </Box>
    );
};

const PhotographerListTranslationLayer = (props: ExternalProps) => {
    let labels: PhotographerListLabels = {
        calendarPhotographersHeading: Translate(
            'orders/list/bookings-calendar/photographers-heading'
        ),
        sms: Translate('orders/list/bookings-calendar/sms'),
        email: Translate('orders/list/bookings-calendar/mail'),
        route: Translate('orders/list/bookings-calendar/route')
    };

    return <PhotographersList {...props} labels={labels} />;
};

export default PhotographerListTranslationLayer;
